import { useNostr } from '@nostrify/react';
import { useInfiniteQuery } from '@tanstack/react-query';
import type { NostrEvent } from '@nostrify/nostrify';

const ARCHIVE_PUBKEY = '90152b7f8dffbcfc462f5c02f122f72e3bae58ccb059431367e2790204e57401';
const PAGE_SIZE = 50;

// Kinds the archive displays. Text notes & quote-posts are both kind 1.
const ARCHIVE_KINDS = [1];

/**
 * Whether a kind-1 note is a REPLY/comment (per NIP-10).
 *
 * A reply references another event via an `e` tag that is NOT marked `mention`.
 * Quote-posts (which we want to KEEP) reference the quoted note via a `q` tag
 * or an `e` tag explicitly marked `mention`, or via a `nostr:` link in content
 * — none of which count as replies here.
 */
export function isReply(event: NostrEvent): boolean {
  for (const tag of event.tags) {
    if (tag[0] !== 'e') continue;
    const marker = tag[3]; // NIP-10 marker: 'root' | 'reply' | 'mention'
    if (marker === 'mention') continue; // a mention/quote, not a reply
    // 'root', 'reply', or a legacy positional e-tag (no marker) = reply/thread.
    return true;
  }
  return false;
}

/**
 * Whether an event belongs in the archive feed.
 *
 * Rules:
 *  - kind 1 only (true reposts are kind 6/16 and are never fetched)
 *  - exclude replies/comments (notes that reply to another event)
 *  - keep original posts and quote-posts
 *  - drop malformed or genuinely empty events
 */
export function isArchiveNote(event: NostrEvent): boolean {
  if (!event.id || typeof event.id !== 'string' || event.id.length !== 64) return false;
  if (!ARCHIVE_KINDS.includes(event.kind)) return false;
  if (isReply(event)) return false; // no comments/replies
  const hasContent = typeof event.content === 'string' && event.content.trim().length > 0;
  const hasTags = Array.isArray(event.tags) && event.tags.length > 0;
  return hasContent || hasTags;
}

interface ArchivePage {
  /** Notes that passed the displayable-content filter */
  notes: NostrEvent[];
  /** Number of raw events the relay returned, before filtering */
  rawCount: number;
  /** Oldest raw timestamp seen, used as the next page cursor */
  oldestRaw: number | null;
}

export function useArchiveNotes() {
  const { nostr } = useNostr();

  return useInfiniteQuery<ArchivePage>({
    queryKey: ['archive', 'notes', ARCHIVE_PUBKEY],
    initialPageParam: undefined as number | undefined,
    queryFn: async ({ pageParam, signal }) => {
      const until = pageParam as number | undefined;
      const filter: Record<string, unknown> = {
        kinds: ARCHIVE_KINDS,
        authors: [ARCHIVE_PUBKEY],
        limit: PAGE_SIZE,
      };
      if (until !== undefined) {
        filter.until = until;
      }
      const events = await nostr.query([filter], { signal: signal ?? AbortSignal.timeout(10000) });
      const sorted = events.sort((a, b) => b.created_at - a.created_at);
      const notes = sorted.filter(isArchiveNote);
      const oldestRaw = sorted.length > 0 ? sorted[sorted.length - 1].created_at : null;
      return { notes, rawCount: sorted.length, oldestRaw };
    },
    getNextPageParam: (lastPage) => {
      // Keep paging as long as we got ANY events (relays often return short
      // pages even when more history exists — don't stop early on that).
      if (lastPage.oldestRaw === null || lastPage.rawCount === 0) {
        return undefined;
      }
      return lastPage.oldestRaw - 1;
    },
    staleTime: 2 * 60 * 1000,
  });
}

export { ARCHIVE_PUBKEY, PAGE_SIZE };
